/**
 * マスターデータ（kumo.config.json）エディタ。
 *
 * 流れ: 読み込み → 編集 → 「Validate & Push」 = ローカルで JSON 構文を確認 → サーバーに PUT（スキーマ検証はサーバーが唯一の正）。
 * 検証エラーはパス付きで一覧し、クリックで該当行へ飛ぶ。production への push は確認を挟む。
 * WHY ブラウザ側でスキーマ検証しないか: 検証ルール（参照整合・タイムゾーン等）をクライアントに複製すると、
 * サーバーだけ更新された時に「画面では通るのに push で落ちる」が起きる。構文だけ先に見る。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, envPath, type Issue } from '../api';
import { ConfirmDialog, ErrorState, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, useT } from '../i18n';
import { lineForPath, locatePaths, parseErrorLocation } from '../jsonLocate';
import { useShell } from './ProjectShell';
import { pushCommand } from '../featureToggle';
import { projectEyebrow } from '../projectNav';

// 雛形は JSON（アプリに配られるデータ）なので表示言語に関わらず英語のまま（翻訳はしない方針: JSON はそのまま）
// 最小の雛形 = version と空の features だけ（2026-09-30 console/english-pass・E5）。
// WHY: 以前は stats の high_score・multiplayer.modes（とランキング・通貨・実績の例）が入ったゲーム向けの雛形だった。
//   MASTER-PLAN §0（ゲームに限らない・芯はサーバーと DB）で、メモのアプリでも何でも最初に開く画面なので、どのアプリにも合う形にする。
//   ほかの節は書かなければサーバーの既定（すべて空・OFF）になる。機能は features.<名前>: true で ON にする（書き方は機能の一覧のカード）。
// 検証: サーバーの configSchema（apps/server/src/modules/config/schema.ts）は version: literal(1)・features: strictObject（既定 {}）・
//   ほかはすべて default を持つ = この形は push で通る（2026-09-30 に configSchema.safeParse で OK を確かめた）。
//   以前の雛形の見せない機能の出し入れ（isLaunchHidden）は、例そのものを無くしたので要らなくなった。
const STARTER = { version: 1, features: {} };

const pretty = (v: unknown) => `${JSON.stringify(v, null, 2)}\n`;

export function ConfigPage() {
  const { project, env } = useShell();
  const url = `${envPath(project.id, env)}/config`;
  const current = useResource<{ version: number; config: unknown }>(url);
  const [text, setText] = useState('');
  const [baseline, setBaseline] = useState('');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [syntax, setSyntax] = useState<{ message: string; line: number | null } | null>(null);
  // text は表示時に翻訳する（push 後に言語を切り替えても通知が今の言語で出るよう、文言ではなくキーと値を持つ）
  const [status, setStatus] = useState<{ tone: 'ok' | 'info'; version: number } | null>(null);
  const { t, tr } = useT();
  const envName = t(`env.inline.${env}`);
  const [busy, setBusy] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!current.data) return;
    // まだ何も push していない環境には雛形を出す（空の {} より「何を書けばいいか」が伝わる）
    const t = current.data.version ? pretty(current.data.config) : pretty(STARTER);
    setText(t);
    setBaseline(current.data.version ? t : '');
    setIssues([]);
    setSyntax(null);
    // status（"Pushed as vN"）は push 直後の読み直しでも残す。消すのは環境を切り替えた時だけ（下の effect）
  }, [current.data]);

  useEffect(() => {
    setStatus(null);
    setPushError(null);
  }, [url]);

  const lines = useMemo(() => text.split('\n').length, [text]);
  const pathLines = useMemo(() => (issues.length ? locatePaths(text) : new Map<string, number>()), [issues, text]);
  const dirty = text !== baseline;
  const errorLines = new Set<number>([
    ...(syntax?.line ? [syntax.line] : []),
    ...issues.map((i) => lineForPath(pathLines, i.path)).filter((n): n is number => n !== null)
  ]);

  const jumpTo = (line: number) => {
    const ta = taRef.current;
    if (!ta) return;
    const idx = text.split('\n').slice(0, line - 1).join('\n').length + (line > 1 ? 1 : 0);
    ta.focus();
    ta.setSelectionRange(idx, idx + (text.split('\n')[line - 1]?.length ?? 0));
    const lh = parseFloat(getComputedStyle(ta).lineHeight) || 20;
    ta.scrollTop = Math.max(0, (line - 4) * lh);
  };

  const check = (): unknown | undefined => {
    setIssues([]);
    setStatus(null);
    try {
      const parsed = JSON.parse(text);
      setSyntax(null);
      return parsed;
    } catch (e) {
      const msg = (e as Error).message;
      const loc = parseErrorLocation(text, msg);
      setSyntax({ message: msg, line: loc?.line ?? null });
      return undefined;
    }
  };

  const push = async () => {
    const parsed = check();
    if (parsed === undefined) return;
    setBusy(true);
    setPushError(null);
    try {
      const r = await api<{ version: number; changed: boolean }>(url, { method: 'PUT', body: parsed });
      setConfirmProd(false);
      setStatus({ tone: r.changed ? 'ok' : 'info', version: r.version });
      current.reload();
    } catch (e) {
      const err = e as ApiError;
      setConfirmProd(false);
      if (err.issues.length) setIssues(err.issues);
      else setPushError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  // production は構文が通ってから確認ダイアログ（壊れた JSON で「本当に？」を聞かない）
  const onPushClick = () => {
    if (env !== 'production') return void push();
    if (check() !== undefined) setConfirmProd(true);
  };

  const statusText = status ? t(status.tone === 'ok' ? 'config.pushed' : 'config.noChanges', { version: String(status.version) }) : '';

  const download = () => {
    const blob = new Blob([text], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'kumo.config.json';
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('config.title')}>
        {current.data && (
          <span className="pill">{current.data.version ? t('config.liveVersion', { version: String(current.data.version) }) : t('config.neverPushed')}</span>
        )}
        <button type="button" className="btn btn-ghost" onClick={download} disabled={!text}>
          {t('config.download', { file: 'kumo.config.json' })}
        </button>
      </PageHeader>

      {current.loading && !current.data && <Spinner />}
      {current.error && <ErrorState error={current.error} what={t('config.title')} onRetry={current.reload} />}
      {current.data && (
        <>
          {current.data.version === 0 && (
            <div className="alert alert-info">
              <strong>{t('config.nothingPushedTitle', { env: envName })}</strong>
              <span>{tr('config.nothingPushedBody', { cmd: <code>{pushCommand(env)}</code> })}</span>
            </div>
          )}
          <div className="editor-grid">
            <div className={`editor ${syntax || issues.length ? 'editor-invalid' : ''}`}>
              <div className="editor-bar">
                <span className="mono small">kumo.config.json</span>
                <span className="small muted">{dirty ? t('config.unsaved') : t('config.inSync')}</span>
              </div>
              <div className="editor-body">
                <div className="gutter" ref={gutterRef} aria-hidden="true">
                  {Array.from({ length: lines }, (_, i) => (
                    <span key={i} className={errorLines.has(i + 1) ? 'gutter-err' : ''}>
                      {i + 1}
                    </span>
                  ))}
                </div>
                <textarea
                  ref={taRef}
                  className="code-input"
                  value={text}
                  spellCheck={false}
                  wrap="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  aria-label={t('config.editorLabel')}
                  aria-invalid={!!syntax || issues.length > 0}
                  onChange={(e) => {
                    setText(e.target.value);
                    setStatus(null);
                  }}
                  onScroll={(e) => {
                    if (gutterRef.current) gutterRef.current.scrollTop = e.currentTarget.scrollTop;
                  }}
                  onKeyDown={(e) => {
                    // Tab は 2 スペース（フォーカス移動は Esc → Tab で抜けられるよう、Esc 後の Tab は既定動作）
                    if (e.key === 'Tab' && !e.shiftKey && !e.altKey && !(e.currentTarget.dataset.escaped === '1')) {
                      e.preventDefault();
                      const ta = e.currentTarget;
                      const { selectionStart: s, selectionEnd: en } = ta;
                      const next = `${text.slice(0, s)}  ${text.slice(en)}`;
                      setText(next);
                      requestAnimationFrame(() => ta.setSelectionRange(s + 2, s + 2));
                    }
                    e.currentTarget.dataset.escaped = e.key === 'Escape' ? '1' : '0';
                  }}
                />
              </div>
            </div>

            <aside className="editor-side">
              <button
                type="button"
                className={`btn btn-block ${env === 'production' ? 'btn-danger' : 'btn-primary'}`}
                disabled={busy || !text.trim()}
                onClick={onPushClick}
              >
                {busy ? t('config.pushing') : t('config.push', { env: envName })}
              </button>
              <p className="small muted">{t('config.tabHint')}</p>

              {status && (
                <div className={`alert ${status.tone === 'ok' ? 'alert-ok' : 'alert-info'}`} role="status">
                  {statusText}
                </div>
              )}
              {pushError && (
                <div className="alert alert-error" role="alert">
                  {pushError}
                </div>
              )}
              {syntax && (
                <div className="alert alert-error" role="alert">
                  <strong>{syntax.line ? t('config.invalidJsonLine', { line: String(syntax.line) }) : t('config.invalidJson')}</strong>
                  <span className="mono small">{syntax.message}</span>
                  {syntax.line && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => jumpTo(syntax.line!)}>
                      {t('config.goToLine')}
                    </button>
                  )}
                </div>
              )}
              {issues.length > 0 && (
                <div className="issues" role="alert">
                  <strong>{t('config.issues', { count: issues.length })}</strong>
                  <ul>
                    {issues.map((i, idx) => {
                      const line = lineForPath(pathLines, i.path);
                      return (
                        <li key={idx}>
                          <button type="button" className="issue" onClick={() => line && jumpTo(line)} disabled={!line}>
                            <span className="mono issue-path">{i.path || t('config.root')}</span>
                            <span>{i.message}</span>
                            {line && <span className="issue-line">{t('config.lineShort', { line: String(line) })}</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {current.data.version > 0 && (
                <p className="small muted">
                  {tr('config.liveNote', { version: <strong>v{current.data.version}</strong> })}
                </p>
              )}
            </aside>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmProd}
        title={t('config.prodTitle')}
        confirmLabel={t('config.prodConfirm')}
        tone="danger"
        busy={busy}
        onClose={() => setConfirmProd(false)}
        onConfirm={() => void push()}
      >
        <p>{t('config.prodBody')}</p>
      </ConfirmDialog>
      <span className="sr-only" aria-live="polite">
        {statusText}
      </span>
    </>
  );
}
